import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "@packages/ui";
import type { CaptchaChallengeResponse } from "@packages/global";
import AuthCaptcha from "./AuthCaptcha";

interface SmsCodeCaptchaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  getCaptcha: () => Promise<CaptchaChallengeResponse>;
  onConfirm: (payload: { captchaId: string; captchaCode: string }) => Promise<void>;
  title: string;
  description: string;
  label: string;
  placeholder: string;
  refreshLabel: string;
  submitLabel: string;
  submittingLabel: string;
  cancelLabel: string;
  requiredMessage: string;
  failedMessage: string;
}

function getErrorMessage(error: any, fallback: string): string {
  return error.response?.data?.error || error.response?.data?.message || fallback;
}

const SmsCodeCaptchaDialog: React.FC<SmsCodeCaptchaDialogProps> = ({
  isOpen,
  onClose,
  getCaptcha,
  onConfirm,
  title,
  description,
  label,
  placeholder,
  refreshLabel,
  submitLabel,
  submittingLabel,
  cancelLabel,
  requiredMessage,
  failedMessage,
}) => {
  const [captchaId, setCaptchaId] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [captchaRefreshKey, setCaptchaRefreshKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleCaptchaChange = useCallback((value: string) => {
    setCaptchaCode(value);
    setError(undefined);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setCaptchaId("");
      setCaptchaCode("");
      setError(undefined);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!captchaCode.trim()) {
      setError(requiredMessage);
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm({ captchaId, captchaCode: captchaCode.trim() });
      onClose();
    } catch (sendError: any) {
      setError(getErrorMessage(sendError, failedMessage));
      setCaptchaRefreshKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      showCloseButton
      closeLabel={cancelLabel}
      maxWidthClassName="max-w-sm"
    >
      <div className="space-y-5">
        <AuthCaptcha
          value={captchaCode}
          error={error}
          refreshSignal={captchaRefreshKey}
          getCaptcha={getCaptcha}
          onChange={handleCaptchaChange}
          onChallengeChange={setCaptchaId}
          label={label}
          placeholder={placeholder}
          refreshLabel={refreshLabel}
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg border border-border-default bg-bg-form px-4 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-elevated"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="h-10 rounded-lg bg-action-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SmsCodeCaptchaDialog;
